import {
  TWITCH_CREATORS,
  TWITCH_LIVE_POLL_INTERVAL_MS,
  TWITCH_WIDGET_AUTO_COLLAPSE_MS,
  TWITCH_WIDGET_SHOW_DELAY_MS,
  twitchDismissedKey,
} from "@/lib/twitch-config";
import type { LiveBroadcastProvider, LiveBroadcastStream } from "@/lib/live-broadcast-types";

export const YOUTUBE_LIVE_CHANNEL_ID = "UCQQ6uFtSRt0ZfBLU1fPFXUw";
export const YOUTUBE_LIVE_CHANNEL_IDS = [
  YOUTUBE_LIVE_CHANNEL_ID,
  "UCfDGySblEGnYLfCwosPjUYw",
] as const;
export const YOUTUBE_LIVE_MIN_POLL_INTERVAL_MS = 6 * 60_000;
export const YOUTUBE_LIVE_POLL_WINDOW_TIME_ZONE = "America/New_York";
export const YOUTUBE_LIVE_POLL_WINDOW_START_MINUTES = 9 * 60 + 30;
export const YOUTUBE_LIVE_POLL_WINDOW_END_MINUTES = 19 * 60;
export const YOUTUBE_LIVE_CACHE_KEY = `live:provider:youtube:${YOUTUBE_LIVE_CHANNEL_IDS.join("+")}:v1`;

export const LIVE_BROADCAST_CLIENT_POLL_INTERVAL_MS = TWITCH_LIVE_POLL_INTERVAL_MS;
export const LIVE_WIDGET_AUTO_COLLAPSE_MS = TWITCH_WIDGET_AUTO_COLLAPSE_MS;
export const LIVE_WIDGET_SHOW_DELAY_MS = TWITCH_WIDGET_SHOW_DELAY_MS;

export interface LiveBroadcastChannelConfig {
  provider: LiveBroadcastProvider;
  channelKey: string;
  channelId: string;
  channelHandle: string | null;
  displayName: string;
  channelUrl: string;
  priority: number;
}

export const YOUTUBE_LIVE_CHANNELS: LiveBroadcastChannelConfig[] = YOUTUBE_LIVE_CHANNEL_IDS.map((channelId, index) => ({
  provider: "youtube",
  channelKey: `youtube:${channelId.toLowerCase()}`,
  channelId,
  channelHandle: null,
  displayName: index === 0 ? "Upside" : "YouTube",
  channelUrl: `https://www.youtube.com/channel/${channelId}`,
  priority: index,
}));

export const YOUTUBE_LIVE_CHANNEL: LiveBroadcastChannelConfig = YOUTUBE_LIVE_CHANNELS[0];

const twitchChannels: LiveBroadcastChannelConfig[] = TWITCH_CREATORS.map((creator, index) => ({
  provider: "twitch",
  channelKey: `twitch:${creator.login.toLowerCase()}`,
  channelId: creator.login.toLowerCase(),
  channelHandle: creator.login.toLowerCase(),
  displayName: creator.displayName,
  channelUrl: creator.url,
  priority: index + YOUTUBE_LIVE_CHANNELS.length,
}));

export const LIVE_BROADCAST_CHANNELS: LiveBroadcastChannelConfig[] = [
  ...YOUTUBE_LIVE_CHANNELS,
  ...twitchChannels,
];

export function liveBroadcastDismissedKey(
  streamOrChannel: Pick<LiveBroadcastStream, "provider" | "channelId" | "channelHandle" | "channelKey">,
): string {
  if (streamOrChannel.provider === "twitch") {
    return twitchDismissedKey((streamOrChannel.channelHandle ?? streamOrChannel.channelId).toLowerCase());
  }

  return `upsider:live_broadcast:dismissed:youtube:${streamOrChannel.channelId.toLowerCase()}`;
}

export function isLiveBroadcastMockMode(): boolean {
  if (process.env.NODE_ENV === "development") return true;
  return process.env.NODE_ENV === "test" && process.env.LIVE_BROADCAST_FORCE_MOCK === "1";
}

export function getLiveBroadcastPriority(stream: Pick<LiveBroadcastStream, "channelKey" | "provider" | "channelId">): number {
  const match = LIVE_BROADCAST_CHANNELS.find(
    (channel) => channel.channelKey === stream.channelKey
      || (channel.provider === stream.provider && channel.channelId.toLowerCase() === stream.channelId.toLowerCase()),
  );
  return match?.priority ?? Number.MAX_SAFE_INTEGER;
}
